// ============================================================
// ByOurConsent.com -- Checkout Page
// Two steps: (1) shipping address, so we can calculate real
// Printful shipping cost + Virginia sales tax before charging
// anything, then (2) accurate order summary + Stripe payment.
// Guest checkout supported -- no account required for the
// address step, but an account is required before payment
// (existing app behavior, unchanged).
// ============================================================

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { loadStripe } from '@stripe/stripe-js';
import useWindowWidth from '../hooks/useWindowWidth';
import {
    Elements,
    PaymentElement,
    useStripe,
    useElements
} from '@stripe/react-stripe-js';
import Nav    from '../components/Nav';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import api    from '../utils/api';
import toast  from 'react-hot-toast';

// Load Stripe outside component to avoid recreating on render
const stripePromise = loadStripe(
    process.env.REACT_APP_STRIPE_PUBLIC_KEY
);

// ============================================================
// Field component
// ============================================================
const Field = ({ label, error, children }) => (
    <div style={{ marginBottom: '1rem' }}>
        <label style={{
            display:       'block',
            fontSize:      '11px',
            fontFamily:    'sans-serif',
            color:         '#6a7080',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            marginBottom:  '5px'
        }}>
            {label}
        </label>
        {children}
        {error && (
            <div style={{
                fontSize:   '12px',
                fontFamily: 'sans-serif',
                color:      '#8b1a1a',
                marginTop:  '3px'
            }}>
                {error}
            </div>
        )}
    </div>
);

const inputStyle = (error) => ({
    width:        '100%',
    background:   '#f5f0e8',
    border:       `0.5px solid ${error ? '#8b1a1a' : '#c8beaa'}`,
    borderRadius: '3px',
    padding:      '9px 12px',
    fontSize:     '14px',
    fontFamily:   'sans-serif',
    color:        '#1e2a3a',
    outline:      'none',
    boxSizing:    'border-box'
});

const sectionBoxStyle = {
    background:   '#f5f0e8',
    border:       '0.5px solid #c8beaa',
    borderRadius: '4px',
    padding:      '1.25rem',
    marginBottom: '1rem'
};

const sectionHeaderStyle = {
    fontSize:      '14px',
    fontWeight:    600,
    color:         '#1e2a3a',
    marginBottom:  '14px',
    paddingBottom: '10px',
    borderBottom:  '0.5px solid #c8beaa',
    fontFamily:    "'EB Garamond', Georgia, serif"
};

// ============================================================
// Step 1: contact + shipping address
// (No Stripe involved yet -- we don't know the real total until
// this is submitted, so no payment intent exists at this point.)
// ============================================================
const AddressStep = ({ initial, onContinue, loadingRates }) => {
    const width    = useWindowWidth();
    const isMobile = width < 768;
    const [form, setForm]     = useState(initial);
    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        const val = e.target.type === 'checkbox'
            ? e.target.checked : e.target.value;
        setForm(p => ({ ...p, [e.target.name]: val }));
        if (errors[e.target.name]) {
            setErrors(p => ({ ...p, [e.target.name]: null }));
        }
    };

    const validate = () => {
        const errs = {};
        if (!form.email)       errs.email      = 'Email required';
        if (!form.first_name)  errs.first_name = 'First name required';
        if (!form.last_name)   errs.last_name  = 'Last name required';
        if (!form.address1)    errs.address1   = 'Address required';
        if (!form.city)        errs.city       = 'City required';
        if (!form.state)       errs.state      = 'State required';
        if (!form.postal_code) errs.postal_code= 'ZIP code required';
        return errs;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const errs = validate();
        if (Object.keys(errs).length) {
            setErrors(errs);
            toast.error('Please fill in all required fields');
            return;
        }
        onContinue(form);
    };

    return (
        <form onSubmit={handleSubmit}>
            {/* Contact */}
            <div style={sectionBoxStyle}>
                <div style={sectionHeaderStyle}>Contact information</div>

                <Field label="Email address" error={errors.email}>
                    <input
                        type="email" name="email"
                        value={form.email} onChange={handleChange}
                        placeholder="your@email.com"
                        style={inputStyle(errors.email)}
                    />
                </Field>

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px' }}>
                    <Field label="First name" error={errors.first_name}>
                        <input
                            type="text" name="first_name"
                            value={form.first_name} onChange={handleChange}
                            placeholder="First"
                            style={inputStyle(errors.first_name)}
                        />
                    </Field>
                    <Field label="Last name" error={errors.last_name}>
                        <input
                            type="text" name="last_name"
                            value={form.last_name} onChange={handleChange}
                            placeholder="Last"
                            style={inputStyle(errors.last_name)}
                        />
                    </Field>
                </div>
            </div>

            {/* Shipping address */}
            <div style={sectionBoxStyle}>
                <div style={sectionHeaderStyle}>Shipping address</div>

                <Field label="Street address" error={errors.address1}>
                    <input
                        type="text" name="address1"
                        value={form.address1} onChange={handleChange}
                        placeholder="123 Main Street"
                        style={inputStyle(errors.address1)}
                    />
                </Field>

                <Field label="Apartment, suite, etc. (optional)">
                    <input
                        type="text" name="address2"
                        value={form.address2} onChange={handleChange}
                        placeholder="Apt 4B"
                        style={inputStyle(false)}
                    />
                </Field>

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '12px' }}>
                    <Field label="City" error={errors.city}>
                        <input
                            type="text" name="city"
                            value={form.city} onChange={handleChange}
                            placeholder="City"
                            style={inputStyle(errors.city)}
                        />
                    </Field>
                    <Field label="State" error={errors.state}>
                        <input
                            type="text" name="state"
                            value={form.state} onChange={handleChange}
                            placeholder="VA"
                            maxLength={2}
                            style={inputStyle(errors.state)}
                        />
                    </Field>
                    <Field label="ZIP code" error={errors.postal_code}>
                        <input
                            type="text" name="postal_code"
                            value={form.postal_code} onChange={handleChange}
                            placeholder="23456"
                            style={inputStyle(errors.postal_code)}
                        />
                    </Field>
                </div>

                <Field label="Country">
                    <select
                        name="country"
                        value={form.country}
                        onChange={handleChange}
                        style={inputStyle(false)}>
                        <option value="US">United States</option>
                        <option value="CA">Canada</option>
                        <option value="GB">United Kingdom</option>
                        <option value="AU">Australia</option>
                    </select>
                </Field>

                <Field label="Gift message (optional)">
                    <textarea
                        name="gift_message"
                        value={form.gift_message}
                        onChange={handleChange}
                        placeholder="Add a personal message..."
                        rows={2}
                        style={{ ...inputStyle(false), resize: 'vertical' }}
                    />
                </Field>
            </div>

            <button
                type="submit"
                disabled={loadingRates}
                style={{
                    width:         '100%',
                    background:    loadingRates ? '#6a1212' : '#8b1a1a',
                    color:         '#f5f0e8',
                    border:        'none',
                    padding:       '15px',
                    fontSize:      '14px',
                    fontFamily:    'sans-serif',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    cursor:        loadingRates ? 'not-allowed' : 'pointer',
                    borderRadius:  '3px'
                }}>
                {loadingRates ? 'Calculating shipping & tax...' : 'Continue to payment →'}
            </button>
        </form>
    );
};

// ============================================================
// Step 2: order summary + Stripe payment
// (Real totals are already known by the time this renders.)
// ============================================================
const PaymentStep = ({ cart, totals, customerInfo, onBack, onSuccess }) => {
    const stripe   = useStripe();
    const elements = useElements();
    const { user, loading: authLoading } = useAuth();

    const [loading,      setLoading]      = useState(false);
    const [elementReady, setElementReady] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!stripe || !elements || !elementReady) {
            toast.error('Payment form not ready. Please wait a moment.');
            return;
        }

        setLoading(true);

        try {
            const { error: stripeError, paymentIntent } =
                await stripe.confirmPayment({
                    elements,
                    confirmParams: {
                        return_url: `${window.location.origin}/order/confirm`,
                        receipt_email: customerInfo.email,
                        shipping: {
                            name:    `${customerInfo.first_name} ${customerInfo.last_name}`,
                            address: {
                                line1:       customerInfo.address1,
                                line2:       customerInfo.address2 || undefined,
                                city:        customerInfo.city,
                                state:       customerInfo.state,
                                postal_code: customerInfo.postal_code,
                                country:     customerInfo.country
                            }
                        }
                    },
                    redirect: 'if_required'
                });

            if (stripeError) {
                toast.error(stripeError.message);
                setLoading(false);
                return;
            }

            // Payment succeeded -- create order in our system
            const orderRes = await api.post('/orders', {
                payment_intent_id:    paymentIntent.id,
                items:                cart,
                customer_email:       customerInfo.email,
                customer_name:        `${customerInfo.first_name} ${customerInfo.last_name}`,
                shipping: {
                    name:        `${customerInfo.first_name} ${customerInfo.last_name}`,
                    address1:    customerInfo.address1,
                    address2:    customerInfo.address2,
                    city:        customerInfo.city,
                    state:       customerInfo.state,
                    postal_code: customerInfo.postal_code,
                    country:     customerInfo.country
                },
                gift_message:         customerInfo.gift_message || null,
                subscribe_newsletter: customerInfo.subscribe
            });

            localStorage.removeItem('boc_cart');
            onSuccess(orderRes.data.order_number);

        } catch (err) {
            toast.error(err.message || 'Payment failed. Please try again.');
            setLoading(false);
        }
    };

    if (authLoading) return null;
    if (!user) return null;

    return (
        <form onSubmit={handleSubmit}>
            <button
                type="button"
                onClick={onBack}
                style={{
                    background: 'transparent', border: 'none', color: '#8b6a3a',
                    fontFamily: 'sans-serif', fontSize: '12px', cursor: 'pointer',
                    padding: 0, marginBottom: '1rem', textDecoration: 'underline'
                }}>
                ← Edit shipping address
            </button>

            <div style={sectionBoxStyle}>
                <div style={sectionHeaderStyle}>Shipping to</div>
                <div style={{ fontFamily: 'sans-serif', fontSize: '13px', color: '#4a5060', lineHeight: 1.8 }}>
                    {customerInfo.first_name} {customerInfo.last_name}<br />
                    {customerInfo.address1}{customerInfo.address2 ? `, ${customerInfo.address2}` : ''}<br />
                    {customerInfo.city}, {customerInfo.state} {customerInfo.postal_code}
                </div>
            </div>

            <div style={{ ...sectionBoxStyle, marginBottom: '1.5rem' }}>
                <div style={sectionHeaderStyle}>Payment</div>

                <PaymentElement
                    onReady={() => setElementReady(true)}
                    options={{
                        layout: 'tabs',
                        wallets: { applePay: 'never', googlePay: 'never' },
                        defaultValues: {
                            billingDetails: {
                                email: customerInfo.email,
                                name:  `${customerInfo.first_name} ${customerInfo.last_name}`
                            }
                        }
                    }}
                />

                <div style={{
                    marginTop: '12px', fontSize: '11px', fontFamily: 'sans-serif',
                    color: '#8a8070', display: 'flex', alignItems: 'center', gap: '6px'
                }}>
                    🔒 Payments secured by Stripe
                </div>
            </div>

            <button
                type="submit"
                disabled={loading || !stripe || !elements || !elementReady}
                style={{
                    width:         '100%',
                    background:    loading ? '#6a1212' : '#8b1a1a',
                    color:         '#f5f0e8',
                    border:        'none',
                    padding:       '15px',
                    fontSize:      '14px',
                    fontFamily:    'sans-serif',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    cursor:        loading ? 'not-allowed' : 'pointer',
                    borderRadius:  '3px'
                }}>
                {loading
                    ? 'Processing payment...'
                    : !elementReady
                    ? 'Loading...'
                    : `Pay $${(totals.total_cents / 100).toFixed(2)}`}
            </button>
        </form>
    );
};

// ============================================================
// CheckoutPage
// ============================================================
const CheckoutPage = () => {
    const navigate  = useNavigate();
    const width     = useWindowWidth();
    const isMobile  = width < 768;
    const { user, loading: authLoading } = useAuth();
    const { cart: contextCart, clearCart } = useCart();

    const [step,          setStep]          = useState('address'); // 'address' | 'payment'
    const [cart]                            = useState(contextCart.length > 0 ? contextCart : []);
    const [customerInfo,  setCustomerInfo]  = useState(null);
    const [clientSecret,  setClientSecret]  = useState(null);
    const [totals,        setTotals]        = useState(null);
    const [loadingRates,  setLoadingRates]  = useState(false);
    const [error,         setError]         = useState(null);

    React.useEffect(() => {
        if (authLoading) return;
        if (!user) {
            navigate('/register', { state: { from: '/checkout', message: 'Create a free account to complete your order and track its status.' } });
        }
    }, [user, authLoading, navigate]);

    React.useEffect(() => {
        if (cart.length === 0) navigate('/cart');
    }, [cart, navigate]);

    const subtotal = cart.reduce(
        (s, i) => s + parseFloat(i.price) * i.quantity, 0
    );

    // Step 1 -> Step 2: now that we have a real address, get the
    // real shipping cost + tax and create the payment intent
    const handleAddressContinue = async (form) => {
        setLoadingRates(true);
        setError(null);
        try {
            const res = await api.post('/orders/create-payment-intent', {
                items: cart,
                shipping: {
                    address1:    form.address1,
                    address2:    form.address2,
                    city:        form.city,
                    state:       form.state,
                    postal_code: form.postal_code,
                    country:     form.country
                }
            });
            if (!res.data.client_secret) {
                toast.error('Could not start checkout. Please try again.');
                return;
            }
            setCustomerInfo(form);
            setClientSecret(res.data.client_secret);
            setTotals(res.data.totals);
            setStep('payment');
        } catch (err) {
            toast.error(err.message || 'Could not calculate shipping. Please try again.');
        } finally {
            setLoadingRates(false);
        }
    };

    const handleSuccess = (orderNumber) => {
        clearCart();
        navigate(`/order/${orderNumber}`);
    };

    if (error) {
        return (
            <>
                <Nav />
                <div style={{
                    minHeight: '60vh', display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center', background: '#f5f0e8', gap: '1rem'
                }}>
                    <div style={{ fontFamily: 'sans-serif', color: '#8b1a1a' }}>{error}</div>
                    <Link to="/cart" style={{ color: '#8b6a3a', fontFamily: 'sans-serif', fontSize: '14px' }}>
                        ← Back to cart
                    </Link>
                </div>
                <Footer />
            </>
        );
    }

    if (authLoading) return null;
    if (!user) return null;

    return (
        <>
            <Helmet><title>Checkout — By Our Consent</title></Helmet>

            <Nav />

            <div style={{
                background: '#1e2a3a', padding: isMobile ? '0.75rem 1rem' : '1.25rem 1.5rem',
                borderBottom: '2px solid #8b1a1a'
            }}>
                <div style={{ maxWidth: '960px', margin: '0 auto' }}>
                    <h1 style={{
                        fontSize: '28px', fontWeight: 600, color: '#e8dfc8',
                        fontFamily: "'EB Garamond', Georgia, serif", margin: 0
                    }}>
                        Checkout
                    </h1>
                </div>
            </div>

            <div style={{
                maxWidth: '960px', margin: '0 auto', padding: isMobile ? '1rem' : '1.5rem',
                display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 320px',
                gap: '2rem', alignItems: 'start'
            }}>

                <div>
                    <div style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#8a8070', marginBottom: '1.25rem' }}>
                        <Link to="/cart" style={{ color: '#8b6a3a', textDecoration: 'none' }}>
                            ← Back to cart
                        </Link>
                    </div>

                    {step === 'address' && (
                        <AddressStep
                            initial={{
                                email:      user?.email || '',
                                first_name: user?.first_name || '',
                                last_name:  user?.last_name || '',
                                address1:   '',
                                address2:   '',
                                city:       '',
                                state:      '',
                                postal_code:'',
                                country:    'US',
                                gift_message: '',
                                subscribe:  true
                            }}
                            onContinue={handleAddressContinue}
                            loadingRates={loadingRates}
                        />
                    )}

                    {step === 'payment' && clientSecret && (
                        <Elements
                            stripe={stripePromise}
                            options={{
                                clientSecret,
                                appearance: {
                                    theme: 'stripe',
                                    variables: {
                                        colorPrimary:    '#8b1a1a',
                                        colorBackground: '#f5f0e8',
                                        colorText:       '#1e2a3a',
                                        colorDanger:     '#8b1a1a',
                                        fontFamily:      'sans-serif',
                                        borderRadius:    '3px'
                                    }
                                },
                                wallets: { applePay: 'never', googlePay: 'never' },
                                paymentMethodCreation: 'manual'
                            }}>
                            <PaymentStep
                                cart={cart}
                                totals={totals}
                                customerInfo={customerInfo}
                                onBack={() => setStep('address')}
                                onSuccess={handleSuccess}
                            />
                        </Elements>
                    )}
                </div>

                {/* Order summary */}
                <div style={{
                    background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '4px',
                    padding: '1.25rem', position: isMobile ? 'static' : 'sticky', top: '72px',
                    order: isMobile ? -1 : 0
                }}>
                    <div style={sectionHeaderStyle}>Order summary</div>

                    {cart.map((item, i) => (
                        <div key={i} style={{
                            display: 'flex', justifyContent: 'space-between',
                            alignItems: 'flex-start', marginBottom: '10px', gap: '10px'
                        }}>
                            <div style={{ flex: 1 }}>
                                <div style={{
                                    fontSize: '13px', color: '#1e2a3a',
                                    fontFamily: "'EB Garamond', Georgia, serif", fontWeight: 600
                                }}>
                                    {item.name}
                                </div>
                                <div style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8a8070' }}>
                                    {[item.size, item.color].filter(Boolean).join(' / ')}
                                    {item.quantity > 1 && ` × ${item.quantity}`}
                                </div>
                            </div>
                            <div style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#1e2a3a', whiteSpace: 'nowrap' }}>
                                ${(parseFloat(item.price) * item.quantity).toFixed(2)}
                            </div>
                        </div>
                    ))}

                    <div style={{ borderTop: '0.5px solid #c8beaa', paddingTop: '12px', marginTop: '4px' }}>
                        {totals ? (
                            [
                                { label: 'Subtotal', value: `$${(totals.subtotal_cents / 100).toFixed(2)}` },
                                { label: 'Shipping', value: totals.shipping_cents === 0 ? 'FREE' : `$${(totals.shipping_cents / 100).toFixed(2)}`, green: totals.shipping_cents === 0 },
                                ...(totals.discount_cents > 0 ? [{ label: 'Discount', value: `-$${(totals.discount_cents / 100).toFixed(2)}`, green: true }] : []),
                                { label: 'Tax', value: `$${(totals.tax_cents / 100).toFixed(2)}` },
                                { label: 'Total', value: `$${(totals.total_cents / 100).toFixed(2)}`, bold: true }
                            ].map(({ label, value, green, bold }) => (
                                <div key={label} style={{
                                    display: 'flex', justifyContent: 'space-between', marginBottom: '6px',
                                    fontSize: bold ? '14px' : '12px', fontFamily: bold ? "'EB Garamond', Georgia, serif" : 'sans-serif',
                                    color: green ? '#3b6d11' : '#1e2a3a',
                                    paddingTop: bold ? '8px' : 0, borderTop: bold ? '0.5px solid #ede8dc' : 'none'
                                }}>
                                    <span style={{ fontWeight: bold ? 600 : 400 }}>{label}</span>
                                    <span style={{ fontWeight: bold || green ? 600 : 400 }}>{value}</span>
                                </div>
                            ))
                        ) : (
                            <>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '12px', fontFamily: 'sans-serif', color: '#6a7080' }}>
                                    <span>Subtotal</span>
                                    <span>${subtotal.toFixed(2)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '12px', fontFamily: 'sans-serif', color: '#8a8070' }}>
                                    <span>Shipping & tax</span>
                                    <span>Enter address to calculate</span>
                                </div>
                            </>
                        )}
                    </div>

                    {process.env.NODE_ENV === 'development' && (
                        <div style={{
                            marginTop: '1rem', padding: '10px', background: '#fffbea',
                            border: '0.5px solid #f0d070', borderRadius: '3px',
                            fontSize: '11px', fontFamily: 'sans-serif', color: '#806020'
                        }}>
                            <strong>Test mode</strong> — Use card:
                            <br />4242 4242 4242 4242
                            <br />Any future date · Any CVC
                        </div>
                    )}
                </div>
            </div>

            <Footer />
        </>
    );
};

export default CheckoutPage;
